import { useCallback, useEffect, useRef, useState } from 'react';
import { EnvironmentInfo } from '@kemu-io/hs-types';
import { useDebouncedCallback } from 'use-debounce';
import useHubDefaultService, { DefaultServices } from '@hooks/useHubDefaultService';
import { useHubContext } from '@common/context/HubContext/HubContext';


type TrackServiceInstallationArgs = {
  status: 'installing' | 'success' | 'launching' | 'failed';
  startedAt?: number;
  completedAt?: number;
  lastUpdatedAt?: number;
  error?: {
    message: string;
    code: string;
  };
  service: {
    name: string;
    version: string;
  };
}

export type ServiceInstallTracker = {
  [nameAndVersion: string]: {
    name: string,
    version: string,
  } & TrackServiceInstallationArgs
};


/**
 * Hook to get the list of services that are being installed.
 * @returns a list of services that are being installed.
 */
const useGetPendingServiceInstallation = () => {
  const { connector: hubConnector, status: connectionStatus } = useHubContext();
  const [findDefaultHubService, defaultServicesReady] = useHubDefaultService();
  const [isLoading, setIsLoading] = useState(false);
  const [pendingServices, setPendingServices] = useState<ServiceInstallTracker>({});

  const debouncedFetchServices = useDebouncedCallback(async () => {
    try {
      const serviceInstaller = findDefaultHubService(DefaultServices.ServiceInstaller.name);
      if (!serviceInstaller) {
        return pendingServices;
      }

      setIsLoading(true);
      const result = await hubConnector.callProcessorHandler<ServiceInstallTracker>(
        serviceInstaller.sessionId,
        {} as EnvironmentInfo,
        DefaultServices.ServiceInstaller.actions.getPendingInstallations
      );

      setIsLoading(false);
      const changed = JSON.stringify(result) !== JSON.stringify(pendingServices);
      if (changed) {
        setPendingServices(result || pendingServices);
      }

      return result || pendingServices;
    } catch (e) {
      console.error('Error getting pending services:', e);
      setIsLoading(false);
      return pendingServices;
    }
  }, 100);


  const forceReload = useCallback(() => {
    debouncedFetchServices();
  }, [debouncedFetchServices]);

  // Fetch services when the component mounts
  useEffect(() => {
    if (connectionStatus === 'acknowledged' && defaultServicesReady) {
      debouncedFetchServices();
    }

    return () => {
      debouncedFetchServices.cancel();
    };
  }, [debouncedFetchServices, connectionStatus, defaultServicesReady]);

  return {
    pendingServices,
    isLoading,
    forceReload,
  };
};

export default useGetPendingServiceInstallation;
